// @view/panels/workspace/AdminUsers.panel.tsx

import EmptyView from "@view/EmptyView";
import React from "react";
import {
  Mail,
  MailCheck,
  MoreHorizontal,
  RefreshCw,
  Send,
  Trash2,
  UserX,
} from "lucide-react";
import * as Option from "@utils/Option";

import * as styles from "@styles/AdminUsers.css";
import "@styles/util/AdminUsers.global.css";
import type {
  AdminUserRole,
  AdminUserStatus,
  AdminUserSummary,
} from "@ps-bridge/models/clients/adminUsers";
import type { IsoTimestamp } from "@ps-bridge/models/brands/isoTimestamp";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import type { AdminUsersViewProps } from "@ps-bridge/contracts/adminUsers";

export type AdminUsersInviteEntry = {
  raw: string;
  email: string;
  name: OptionTs<string>;
};

export type AdminUsersPanelViewProps = AdminUsersViewProps;

export type AdminUsersPanelProps = {
  viewProps: AdminUsersPanelViewProps;
  inviteEntries: AdminUsersInviteEntry[];
  onEmailModalSubmit: IOts<Ok>;
  okValue: Ok;
  runIO: (effect: IOts<Ok>) => Ok;
};

type AdminUserMaybeTimestamp = OptionTs<IsoTimestamp>;
const unwrapAdminUserRole = (value: AdminUserRole): string => String(value);
const unwrapAdminUserStatus = (value: AdminUserStatus): string => String(value);
const unwrapEmail = (value: string): string => String(value);
const unwrapInviteRejectionReason = (value: string): string => String(value);
const unwrapUUID = (value: string): string => String(value);
const makeAdminUserRole = (value: string): AdminUserRole => value as AdminUserRole;

const formatDate = (value?: string): string => {
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toISOString().slice(0, 10);
};

const formatMaybeDate = (value: AdminUserMaybeTimestamp): string =>
  Option.fold(
    value,
    () => "-",
    (timestamp) => formatDate(String(timestamp)),
  );

const roleLabel = (role: AdminUserRole): string => {
  if (unwrapAdminUserRole(role) === "SuperAdmin") {
    return "Super admin";
  }
  if (unwrapAdminUserRole(role) === "Admin") {
    return "Admin";
  }
  return "User";
};

const statusLabel = (status: AdminUserStatus): string => {
  if (unwrapAdminUserStatus(status) === "Pending") {
    return "Pending";
  }
  if (unwrapAdminUserStatus(status) === "Suspended") {
    return "Suspended";
  }
  return "Active";
};

const AdminUsersPanel: React.FC<AdminUsersPanelProps> = ({
  viewProps,
  inviteEntries,
  onEmailModalSubmit,
  okValue,
  runIO,
}) => {
  const seatSummaryView = Option.fold(
    viewProps.seatLimit,
    () => <EmptyView />,
    (total: number) =>
      Option.fold(
        viewProps.seatsUsed,
        () => <EmptyView />,
        (used: number) => (
          <div className={styles.seatSummary} data-testid="admin-users-seat-summary">
            <span className={styles.seatUsed}>{used}</span>
            <span className={styles.seatLabel}>Occupied</span>
            <span className={styles.seatDivider}>•</span>
            <span className={styles.seatTotal}>{Math.max(total - used, 0)}</span>
            <span className={styles.seatLabel}>Available</span>
          </div>
        ),
      ),
  );

  const loadErrorView = Option.fold(
    viewProps.loadError,
    () => <EmptyView />,
    (message) => <div className={styles.errorBanner} data-testid="admin-users-error-banner">{message}</div>,
  );

  const inviteResultView = Option.fold(
    viewProps.inviteResult,
    () => <EmptyView />,
    (result) => {
      const rejectionPhase = result.rejected.length > 0 ? "visible" : "hidden";
      const rejectionView = {
        hidden: <EmptyView />,
        visible: <span>{result.rejected.length} rejected.</span>,
      } as const satisfies Record<"hidden" | "visible", React.ReactElement>;
      return (
        <div className={styles.inviteResult} data-testid="admin-users-invite-result">
          <span>
            Sent {result.accepted.length} invite
            {result.accepted.length === 1 ? "" : "s"}.
          </span>
          {rejectionView[rejectionPhase]}
        </div>
      );
    },
  );

  const rejectedListView = Option.fold(
    viewProps.inviteResult,
    () => <EmptyView />,
    (result) => {
      const rejectedPhase = result.rejected.length === 0 ? "hidden" : "visible";
      const rejectedView = {
        hidden: <EmptyView />,
        visible: (
          <div className={styles.rejectedList} data-testid="admin-users-invite-rejected">
            {result.rejected.map((rejection) => (
              <div
                key={unwrapEmail(rejection.email)}
                className={styles.rejectedItem}
                data-testid="admin-users-invite-rejected-item"
              >
                <span>{unwrapEmail(rejection.email)}</span>
                <span className={styles.rejectedReason}>
                  {unwrapInviteRejectionReason(rejection.reason)}
                </span>
              </div>
            ))}
          </div>
        ),
      } as const satisfies Record<"hidden" | "visible", React.ReactElement>;
      return rejectedView[rejectedPhase];
    },
  );

  const emailModalView = Option.fold(
    viewProps.emailModalUser,
    () => <EmptyView />,
    (emailModalUser) => {
      return (
        <div className={styles.modalBackdrop} onClick={() => runIO(viewProps.onCloseEmailModal(okValue))}>
          <div
            className={styles.modalCard}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            data-testid="admin-users-email-modal"
          >
            <div className={styles.modalHeader}>
              <h3>Change user email</h3>
              <p>
                {unwrapAdminUserStatus(emailModalUser.status) === "Pending"
                  ? "We'll update the invite and resend it to the new address."
                  : "Update the user’s primary login email."}
              </p>
            </div>
            <div className={styles.modalBody}>
              <label className={styles.modalLabel} htmlFor="change-user-email">
                New email address
              </label>
              <input
                id="change-user-email"
                className={styles.modalInput}
                value={viewProps.emailModalValue}
                onChange={(event) => runIO(viewProps.onEmailModalValueChange(event.target.value))}
                placeholder="user@company.com"
                data-testid="admin-users-email-input"
              />
              {Option.fold(
                viewProps.emailModalError,
                () => <EmptyView />,
                (message) => (
                  <div className={styles.modalError} data-testid="admin-users-email-error">
                    {message}
                  </div>
                ),
              )}
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.ghostButton}
                onClick={() => runIO(viewProps.onCloseEmailModal(okValue))}
                data-testid="admin-users-email-cancel"
              >
                Cancel
              </button>
              <button
                className={styles.primaryButton}
                onClick={() => runIO(onEmailModalSubmit)}
                disabled={viewProps.emailModalSubmitting}
                data-testid="admin-users-email-submit"
              >
                {unwrapAdminUserStatus(emailModalUser.status) === "Pending"
                  ? "Change Email + Resend"
                  : "Change Email"}
              </button>
            </div>
          </div>
        </div>
      );
    },
  );

  const inviteEntriesPhase = inviteEntries.length === 0 ? "empty" : "filled";
  const inviteEntriesView = {
    empty: <span className={styles.emptyHint} data-testid="admin-users-invite-empty">No valid emails detected yet.</span>,
    filled: (
      <>
        {inviteEntries.map((entry) => (
          <span
            key={entry.email}
            className={styles.emailChip}
            title={entry.email}
            data-testid="admin-users-invite-chip"
          >
            {Option.fold(entry.name, () => entry.email, (value) => `${value} (${entry.email})`)}
          </span>
        ))}
      </>
    ),
  } as const satisfies Record<"empty" | "filled", React.ReactElement>;

  const emptyUsersPhase = viewProps.users.length === 0 ? "empty" : "filled";
  const emptyUsersView = {
    empty: <div className={styles.emptyState}>No users found.</div>,
    filled: <EmptyView />,
  } as const satisfies Record<"empty" | "filled", React.ReactElement>;

  const renderUserRow = (user: AdminUserSummary) => {
    const userId = unwrapUUID(user.id);
    const isSelf = Option.fold(
      viewProps.currentEmail,
      () => false,
      (email) => unwrapEmail(user.email).toLowerCase() === email.toLowerCase(),
    );
    const showRoleSelect =
      viewProps.isSuperAdmin
      && (!Option.fold(viewProps.currentEmail, () => false, () => true) || !isSelf);
    const allowUserActions = !viewProps.pendingEmailValidation;
    const allowRoleSelect = showRoleSelect && allowUserActions;
    const lastUpdated =
      unwrapAdminUserStatus(user.status) === "Pending"
        ? formatMaybeDate(user.invitedAt)
        : unwrapAdminUserStatus(user.status) === "Suspended"
          ? formatMaybeDate(user.suspendedAt)
          : formatMaybeDate(user.activatedAt);
    const changeEmailLabel =
      unwrapAdminUserStatus(user.status) === "Pending"
        ? "Change User Email + Resend Invite"
        : "Change User Email";
    const changeEmailIcon = unwrapAdminUserStatus(user.status) === "Pending" ? MailCheck : Mail;
    const statusPhase = unwrapAdminUserStatus(user.status) as "Pending" | "Active" | "Suspended";

    const rolePhase = allowRoleSelect ? "select" : "label";
    const roleView = {
      select: (
        <select
          className={styles.roleSelect}
          value={unwrapAdminUserRole(user.role)}
          onChange={(event) =>
            runIO(viewProps.onRoleChange({
              user,
              nextRole: makeAdminUserRole(event.target.value),
            }))
          }
          data-testid={`admin-users-role-select-${userId}`}
        >
          <option value="User">User</option>
          <option value="Admin">Admin</option>
          <option value="SuperAdmin">Super admin</option>
        </select>
      ),
      label: (
        <span className={styles.roleLabel} data-testid={`admin-users-role-label-${userId}`}>
          {roleLabel(user.role)}
        </span>
      ),
    } as const satisfies Record<"select" | "label", React.ReactElement>;

    const statusActionsView = {
      Pending: (
        <>
          <button
            className={styles.menuItem}
            onClick={() => {
              runIO(viewProps.onMenuUserIdChange(Option.none()));
              runIO(viewProps.onResendInvite(user));
            }}
            data-testid={`admin-users-action-resend-${userId}`}
          >
            <Send size={16} />
            Resend invite
          </button>
          <button
            className={styles.menuItemDanger}
            onClick={() => {
              runIO(viewProps.onMenuUserIdChange(Option.none()));
              runIO(viewProps.onRemovePending(user));
            }}
            data-testid={`admin-users-action-remove-${userId}`}
          >
            <Trash2 size={16} />
            Remove user
          </button>
        </>
      ),
      Active: (
        <button
          className={styles.menuItemDanger}
          onClick={() => {
            runIO(viewProps.onMenuUserIdChange(Option.none()));
            runIO(viewProps.onSuspendUser(user));
          }}
          data-testid={`admin-users-action-suspend-${userId}`}
        >
          <UserX size={16} />
          Suspend user
        </button>
      ),
      Suspended: (
        <>
          <button
            className={styles.menuItem}
            onClick={() => {
              runIO(viewProps.onMenuUserIdChange(Option.none()));
              runIO(viewProps.onReactivateUser(user));
            }}
            data-testid={`admin-users-action-reactivate-${userId}`}
          >
            <RefreshCw size={16} />
            Reactivate
          </button>
          <button
            className={styles.menuItemDanger}
            onClick={() => {
              runIO(viewProps.onMenuUserIdChange(Option.none()));
              runIO(viewProps.onDeleteUser(user));
            }}
            data-testid={`admin-users-action-delete-${userId}`}
          >
            <Trash2 size={16} />
            Delete user
          </button>
        </>
      ),
    } as const satisfies Record<"Pending" | "Active" | "Suspended", React.ReactElement>;

    const statusActionPhase = isSelf ? "hidden" : "visible";
    const statusActionView = {
      hidden: <EmptyView />,
      visible: statusActionsView[statusPhase],
    } as const satisfies Record<"hidden" | "visible", React.ReactElement>;

    const menuPhase = Option.fold(
      viewProps.menuUserId,
      () => "closed",
      (value) => (value === unwrapUUID(user.id) ? "open" : "closed"),
    ) as "closed" | "open";
    const menuView = {
      closed: <EmptyView />,
      open: (
        <div
          className={styles.menuDropdown}
          role="menu"
          onClick={(event) => event.stopPropagation()}
          data-testid={`admin-users-menu-${userId}`}
        >
          <button
            className={styles.menuItem}
            onClick={() => {
              runIO(viewProps.onMenuUserIdChange(Option.none()));
              runIO(viewProps.onOpenEmailModal(user));
            }}
            data-testid={`admin-users-action-change-email-${userId}`}
          >
            {React.createElement(changeEmailIcon, { size: 16 })}
            {changeEmailLabel}
          </button>
          {statusActionView[statusActionPhase]}
        </div>
      ),
    } as const satisfies Record<"closed" | "open", React.ReactElement>;

    const actionPhase = allowUserActions ? "menu" : "blocked";
    const actionView = {
      blocked: (
        <span
          className={styles.pendingActionHint}
          data-testid={`admin-users-action-blocked-${userId}`}
        >
          Verify email to manage
        </span>
      ),
      menu: (
        <div className={styles.menuWrapper}>
          <button
            className={styles.menuButton}
            onClick={(event) => {
              event.stopPropagation();
              runIO(
                viewProps.onMenuUserIdChange(
                  Option.fold(
                    viewProps.menuUserId,
                    () => Option.some(userId),
                    (value) => (value === userId ? Option.none() : Option.some(userId)),
                  ),
                ),
              );
            }}
            data-testid={`admin-users-actions-${userId}`}
          >
            <MoreHorizontal size={18} />
          </button>
          {menuView[menuPhase]}
        </div>
      ),
    } as const satisfies Record<"blocked" | "menu", React.ReactElement>;

    const userMetaPhase = unwrapAdminUserStatus(user.status) === "Pending" ? "invited" : "active";
    const userMetaView = {
      invited: "Invited",
      active: "Seat active",
    } as const satisfies Record<"invited" | "active", string>;

    return (
      <div key={user.id} className={styles.tableRow} data-testid={`admin-users-row-${userId}`}>
        <div>
          <div className={styles.userEmail} data-testid={`admin-users-email-${userId}`}>
            {unwrapEmail(user.email)}
          </div>
          <div className={styles.userMeta}>{userMetaView[userMetaPhase]}</div>
        </div>
        <div>{roleView[rolePhase]}</div>
        <div>
          <span
            className={`${styles.statusBadge} ${
              styles[`status${unwrapAdminUserStatus(user.status)}` as keyof typeof styles]
            }`}
            data-testid={`admin-users-status-${userId}`}
          >
            {statusLabel(user.status)}
          </span>
        </div>
        <div className={styles.dateCell}>{lastUpdated}</div>
        <div className={styles.actionCell}>{actionView[actionPhase]}</div>
      </div>
    );
  };

  return (
    <>
      <div className={styles.container}>
        <header className={styles.header}>
          <p className={styles.kicker}>Administration</p>
          <h1 className={styles.title}>Users</h1>
          <p className={styles.subtitle}>
            Invite teammates, track their access, and keep your organization within its seat limit.
          </p>
        </header>

        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2 className={styles.panelTitle}>Invite users</h2>
              <p className={styles.panelHint}>
                Paste a list of emails from Gmail or Outlook and we will normalize it automatically.
              </p>
            </div>
            <button
              className={styles.primaryButton}
              onClick={() => runIO(viewProps.onInvite(inviteEntries.map((entry) => entry.raw)))}
              disabled={inviteEntries.length === 0}
              data-testid="admin-users-invite-send"
            >
              <Send size={16} />
              Send invites
            </button>
          </div>
          <div className={styles.panelBody}>
            <textarea
              className={styles.inviteInput}
              placeholder="alex@company.com, Brianna <brianna@company.com>, dev@partner.org"
              value={viewProps.inviteText}
              onChange={(event) => runIO(viewProps.onInviteTextChange(event.target.value))}
              data-testid="admin-users-invite-input"
            />
            <div className={styles.inviteMeta}>
              <div className={styles.emailChips} data-testid="admin-users-invite-chips">
                {inviteEntriesView[inviteEntriesPhase]}
              </div>
              {inviteResultView}
            </div>
            {rejectedListView}
          </div>
        </section>

        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2 className={styles.panelTitle}>Current users</h2>
              <p className={styles.panelHint}>Pending invites are shown alongside active seats.</p>
            </div>
            {seatSummaryView}
            <button
              className={styles.ghostButton}
              onClick={() => runIO(viewProps.onRefresh(okValue))}
              data-testid="admin-users-refresh"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
          <div className={styles.panelBody}>
            {loadErrorView}
            <div className={styles.table} data-testid="admin-users-table">
              <div className={styles.tableHeader}>
                <span>User</span>
                <span>Role</span>
                <span>Status</span>
                <span>Last updated</span>
                <span className={styles.actionHeader}>Actions</span>
              </div>
              {viewProps.users.map((user) => renderUserRow(user))}
              {emptyUsersView[emptyUsersPhase]}
            </div>
          </div>
        </section>
      </div>
      {emailModalView}
    </>
  );
};

export default AdminUsersPanel;
